import { useMemo } from 'react'

import {
  type FiltersDefinition,
  GroupingDefinition,
  PaginatedResponse,
} from '@factorialco/f0-react'
import {
  DataCollectionPaginatedFetchOptions,
  type ItemActionsDefinition,
  type NavigationFiltersDefinition,
  OneDataCollection,
  type SortingsDefinition,
  type SummariesDefinition,
  useDataCollectionSource,
} from '@factorialco/f0-react/dist/experimental'
import {
  DocumentsPageFileCategoryFragment,
  DocumentsPageFileFragment,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

interface Props {
  category: DocumentsPageFileCategoryFragment & {
    filesConnection: {
      nodes: DocumentsPageFileFragment[]
      totalCount?: number | null
    }
  }
}

const FilesCategoryList = ({ category }: Props) => {
  const files = useMemo(() => category.filesConnection.nodes, [category.filesConnection.nodes])
  const exportedFiles = category.categoryType === 'exported'

  const source = useDataCollectionSource<
    DocumentsPageFileFragment,
    FiltersDefinition<string>,
    SortingsDefinition,
    SummariesDefinition,
    ItemActionsDefinition<DocumentsPageFileFragment>,
    NavigationFiltersDefinition,
    GroupingDefinition<DocumentsPageFileFragment>
  >(
    {
      dataAdapter: {
        fetchData: ({
          pagination,
        }: DataCollectionPaginatedFetchOptions<
          FiltersDefinition<string>,
          NavigationFiltersDefinition
        >): Promise<PaginatedResponse<DocumentsPageFileFragment>> => {
          const currentPage = pagination?.currentPage || 1
          const perPage = 10
          const start = (currentPage - 1) * perPage
          const end = start + perPage
          const paginatedData = files.slice(start, end)

          return Promise.resolve({
            type: 'pages',
            records: paginatedData,
            total: files.length,
            currentPage,
            perPage,
            pagesCount: Math.ceil(files.length / perPage),
          })
        },
        perPage: 10,
        paginationType: 'pages',
      },
    },
    [files]
  )

  const emptyStates = {
    'no-data': {
      title: i18n.t('bookkeepers.clients.payroll_cycles.documents.empty_state.title'),
      description: exportedFiles
        ? i18n.t('bookkeepers.clients.payroll_cycles.documents.empty_state.description_exported')
        : i18n.t('bookkeepers.clients.payroll_cycles.documents.empty_state.description_shared'),
    },
  }

  return (
    <OneDataCollection
      source={source}
      emptyStates={emptyStates}
      visualizations={[
        {
          type: 'list',
          options: {
            itemDefinition: (item: DocumentsPageFileFragment) => ({
              title: item.fileName,
            }),
            fields: [
              {
                label: 'created at',
                render: (item: DocumentsPageFileFragment) => ({
                  type: 'text',
                  value: item.createdAt,
                }),
              },
            ],
          },
        },
      ]}
    />
  )
}

export default FilesCategoryList
